import React from 'react';
import { Plane, Ship, FileText, Clock, MapPin, AlertCircle } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import '../styles/HowToGetThere.css';

import L from 'leaflet';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

const HowToGetThere = () => {
  const travelOptions = [
    {
      title: "Via Flight + Boat (Recommended)",
      steps: [
        {
          step: 1,
          title: "Fly to Kochi (Cochin)",
          icon: <Plane className="step-icon" />,
          description: "Your journey begins at Cochin International Airport (COK) in Kerala. This is the main gateway to Lakshadweep with connections from major Indian cities including Delhi, Mumbai, Bangalore, and Chennai.",
          duration: "Varies by origin city"
        },
        {
          step: 2,
          title: "Fly to Agatti Island",
          icon: <Plane className="step-icon" />,
          description: "From Kochi, take a direct flight to Agatti Airport, the nearest airport to Kadmat Island. Flights are operated by Indian Airlines and Alliance Air. It's recommended to book these flights well in advance as seats are limited.",
          duration: "~1.5 hours flight time"
        },
        {
          step: 3,
          title: "Boat to Kadmat Island",
          icon: <Ship className="step-icon" />,
          description: "From Agatti, take a government-approved boat to Kadmat Island. Boats operate on specific days (typically Monday, Wednesday, and Friday). The scenic boat ride offers stunning views of the turquoise waters.",
          duration: "2-3 hours boat ride"
        }
      ]
    },
    {
      title: "Via Seaplane (Fastest)",
      steps: [
        {
          step: 1,
          title: "Fly to Kochi (Cochin)",
          icon: <Plane className="step-icon" />,
          description: "Your journey begins at Cochin International Airport (COK) in Kerala.",
          duration: "Varies by origin city"
        },
        {
          step: 2,
          title: "Seaplane to Kadmat",
          icon: <Plane className="step-icon" />,
          description: "Take a seaplane directly from Kochi to Kadmat Island. This scenic option offers breathtaking aerial views of the islands and turquoise lagoons. Limited availability - book well in advance.",
          duration: "~2 hours direct"
        }
      ]
    }
  ];

  const permitInfo = {
    requirement: "All visitors to Lakshadweep (except natives) must obtain an Entry Permit before traveling to the islands.",
    submitTo: {
      designation: "Collector UTL",
      company: "Lakshadweep Administration",
      building: "Collectors Block",
      street: "Secretariat",
      landmark: "Secratariat",
      locality: "Kavaratti",
      state: "Lakshadweep",
      district: "Lakshadweep",
      taluka: "Kavaratti"
    },
    process: [
      {
        step: "Police Clearance Certificate (PCC)",
        details: "Apply for a PCC from your local police station. This certifies you have no criminal record.",
        timeline: "2-3 weeks"
      },
      {
        step: "Submit Entry Permit Application",
        details: "Submit your completed application form along with required documents to the Lakshadweep Administration Office in Kochi or apply online through the official ePermit portal.",
        timeline: "7-10 days after PCC"
      },
      {
        step: "Book Through Authorized Operators",
        details: "Most accommodations and tour operators will assist with the permit process as part of their booking services. This is highly recommended.",
        timeline: "Included in package"
      }
    ],
    documents: [
      "Completed Entry Permit Application Form",
      "Police Clearance Certificate (PCC)",
      "Passport-sized photographs",
      "Valid ID proof (Aadhaar, Passport, etc.)",
      "Confirmed accommodation booking",
      "Travel itinerary",
      "Purpose of visit"
    ],
    fees: "₹50 per application"
  };

  const importantNotes = [
    "Book your flights and accommodation at least 4-6 weeks in advance, especially during peak season (October to May)",
    "The permit process can take 3-4 weeks total, so plan accordingly",
    "Carry printed copies of your permit and ID at all times",
    "Best time to visit is October to May when weather conditions are favorable"
  ];

  const kadmatCoords = [11.194286057525934, 72.76393003656378];

  return (
    <div className="how-to-get-there-page">
      <div className="about-content-wrapper">
        {/* Hero Section with Travel Steps Inside */}
        <div className="travel-hero">
          <div className="travel-title">
            <h1>How to Get There</h1>
          </div>
          
          <div className="travel-description">
            <p>
              Planning your journey to Turtle's Bay Resort on Kadmat Island, Lakshadweep? 
              Here's everything you need to know about reaching this tropical paradise.
            </p>
            <div className="divider"></div>
          </div>

          {/* Travel Steps - NOW INSIDE HERO */}
          <div className="travel-steps-container">
            {travelOptions.map((option, idx) => (
              <div key={idx} className="travel-option">
                <h3 className="option-title">{option.title}</h3>
                <div className="steps-grid">
                  {option.steps.map((step) => (
                    <div key={step.step} className="step-card">
                      <div className="step-number">Step {step.step}</div>
                      <div className="step-icon-container">
                        {step.icon}
                      </div>
                      <h4 className="step-title">{step.title}</h4>
                      <p className="step-description">{step.description}</p>
                      <div className="step-duration">
                        <Clock className="duration-icon" />
                        <span>{step.duration}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        

        {/* Permit Section */}
        <div className="permit-section">
          <h2 className="section-title">Entry Permit Requirements</h2>
          
          <div className="permit-alert">
            <AlertCircle className="alert-icon" />
            <div className="alert-content">
              <strong>Important:</strong> {permitInfo.requirement}
            </div>
          </div>

          <div className="permit-content-grid">
            {/* Permit Process */}
            <div className="permit-card">
              <div className="permit-card-header">
                <FileText className="card-icon" />
                <h3>Application Process</h3>
              </div>
              <div className="permit-process-list">
                {permitInfo.process.map((item, index) => (
                  <div key={index} className="process-item">
                    <div className="process-header">
                      <div className="process-number">{index + 1}</div>
                      <h4>{item.step}</h4>
                    </div>
                    <p className="process-details">{item.details}</p>
                    <div className="process-timeline">
                      <Clock className="timeline-icon" />
                      <span>Timeline: {item.timeline}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Required Documents */}
            <div className="permit-card">
              <div className="permit-card-header">
                <FileText className="card-icon" />
                <h3>Required Documents</h3>
              </div>
              <ul className="documents-list">
                {permitInfo.documents.map((doc, index) => (
                  <li key={index}>
                    <div className="doc-bullet"></div>
                    {doc}
                  </li>
                ))}
              </ul>
              
              <div className="permit-details-box">
                <div className="detail-row">
                  <strong>Application Fee:</strong>
                  <span>{permitInfo.fees}</span>
                </div>
              </div>
            </div>
            </div>
            {/* Submission Address from Form */}
            <div className="permit-details">
              <div className="permit-card-header">
                <MapPin className="card-icon" />
                <h3>Details to be entered for PCC Form</h3>
              </div>
              <div className="address-grid">
                <div className="address-item">
                  <strong>Designation: </strong>
                  <span>{permitInfo.submitTo.designation}</span>
                </div>
                <div className="address-item">
                  <strong>Company: </strong>
                  <span>{permitInfo.submitTo.company}</span>
                </div>
                <div className="address-item">
                  <strong>Building: </strong>
                  <span>{permitInfo.submitTo.building}</span>
                </div>
                <div className="address-item">
                  <strong>Street: </strong>
                  <span>{permitInfo.submitTo.street}</span>
                </div>
                <div className="address-item">
                  <strong>Landmark: </strong>
                  <span>{permitInfo.submitTo.landmark}</span>
                </div>
                <div className="address-item">
                  <strong>Locality: </strong>
                  <span>{permitInfo.submitTo.locality}</span>
                </div>
                <div className="address-item">
                  <strong>State: </strong>
                  <span>{permitInfo.submitTo.state}</span>
                </div>
                <div className="address-item">
                  <strong>District: </strong>
                  <span>{permitInfo.submitTo.district}</span>
                </div>
                <div className="address-item">
                  <strong>Taluka: </strong>
                  <span>{permitInfo.submitTo.taluka}</span>
                </div>
              </div>
              <div className="address-item">
                <strong>Full Address: </strong>
                <span>{permitInfo.submitTo.designation}, {permitInfo.submitTo.building}, {permitInfo.submitTo.street}, {permitInfo.submitTo.landmark}, {permitInfo.submitTo.locality}, {permitInfo.submitTo.district}, {permitInfo.submitTo.state}</span>
              </div>
            </div>

          {/* Online Application */}
          <div className="online-application-box">
            <h3>Apply Online</h3>
            <p>You can apply for your entry permit online through the official Lakshadweep ePermit portal:</p>
            <a href="https://epermit.utl.gov.in/" target="_blank" rel="noopener noreferrer" className="portal-button">
              Visit ePermit Portal
            </a>
          </div>
        </div>

        {/* Important Notes */}
        <div className="important-notes-section">
          <h2 className="section-title">Important Travel Tips</h2>
          <div className="notes-grid">
            {importantNotes.map((note, index) => (
              <div key={index} className="note-card">
                <AlertCircle className="note-icon" />
                <p>{note}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Map Section */}
        <div className="map-section">
          <h2 className="section-title">Location</h2>
          <div className="map-card">
            <div className="map-info">
              <MapPin className="map-icon" />
              <div>
                <h3>Kadmat Island, Lakshadweep</h3>
                <p>Located in the Arabian Sea, approximately 400 km off the coast of Kerala</p>
              </div>
            </div>
            <div className="map-view">
            <MapContainer
                center={kadmatCoords}
                zoom={13}
                scrollWheelZoom={false}
                style={{ height: "350px", width: "100%", borderRadius: "12px", marginTop: "1rem" }}
            >
                <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>'
                />
                <Marker position={kadmatCoords}>
                <Popup>Turtles Bay Resort 🐢<br />Kadmat Island, Lakshadweep</Popup>
                </Marker>
            </MapContainer>
            </div>
          </div>
        </div>

        {/* Contact Help Section */}
        <div className="help-section">
          <h2 className="section-title">Need Help Planning Your Trip?</h2>
          <p className="help-text">
            Our team at Turtle's Bay Resort can assist you with the entire travel and permit process. 
            We work with authorized travel operators to make your journey smooth and hassle-free.
          </p>
          <button className="contact-button">Contact Us for Assistance</button>
        </div>
        </div>
      </div>
    </div>
  );
};

export default HowToGetThere;